'use client';

import { useEffect, useRef } from 'react';
import {
  resolveSearchEntityFromTemplate,
  trackSearchEntityPageView,
} from 'lib/search/analytics';

type UseTrackEntityPageViewArgs = {
  itemId?: string | null;
  templateName?: string | null;
  templateId?: string | null;
  /** Optional override when template mapping is insufficient. */
  entity?: string | null;
  enabled?: boolean;
};

/**
 * Fires Sitecore Search `entityView` once per entity page (detail pages).
 * Required for CEC analytics / personalization — not auto-tracked by Cloud SDK.
 */
export function useTrackEntityPageView({
  itemId,
  templateName,
  templateId,
  entity: entityOverride,
  enabled = true,
}: UseTrackEntityPageViewArgs): void {
  const lastTrackedKeyRef = useRef<string>('');

  useEffect(() => {
    if (!enabled || !itemId) {
      return;
    }

    const entity =
      entityOverride || resolveSearchEntityFromTemplate(templateName, templateId) || null;
    if (!entity) {
      return;
    }

    const trackKey = `${entity}:${itemId}`;
    if (lastTrackedKeyRef.current === trackKey) {
      return;
    }
    lastTrackedKeyRef.current = trackKey;

    void trackSearchEntityPageView({
      entity: {
        id: itemId,
        entity,
        uri: typeof window !== 'undefined' ? window.location.pathname : undefined,
      },
    });
  }, [enabled, itemId, templateName, templateId, entityOverride]);
}
